/**
 * Acknowledges an OncoEMR task (clears `is_updated` with optimistic concurrency).
 * @module ackOncoemrTask
 */

import type {
  AckOncoemrTaskRequest,
  AckOncoemrTaskResponse,
} from "../../data-model/oncoemrTasksModel";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { formatErrorDetail } from "../throwApiError";
import { getBearerToken } from "./bearerToken";
import { oncoemrTaskAckStaleErrorFromBody } from "./oncoemrTaskAckErrors";

/**
 * Builds the URL for `POST /oncoemr-tasks/{task_row_id}/ack`.
 *
 * @param taskRowId - Primary key of the `tasks` row (`tasks.id`).
 * @returns Absolute request URL.
 */
export const buildOncoemrTaskAckUrl = (taskRowId: string): string =>
  `${API_ENDPOINTS.ONCOEMR_TASKS}/${encodeURIComponent(taskRowId)}/ack`;

/**
 * Clears the task `is_updated` badge when the user has viewed the thread.
 *
 * @param taskRowId - `tasks.id` for the selected list row.
 * @param body - Org scope and expected row version for optimistic locking.
 * @param signal - Optional abort signal.
 * @returns Updated row id, version, and `is_updated: false`.
 * @throws {OncoemrTaskAckStaleError} When the row version changed (409).
 * @throws Error When the task is missing (404) or the request otherwise fails.
 */
export const ackOncoemrTask = async (
  taskRowId: string,
  body: AckOncoemrTaskRequest,
  signal?: AbortSignal,
): Promise<AckOncoemrTaskResponse> => {
  const bearerToken = await getBearerToken();

  const response = await fetch(buildOncoemrTaskAckUrl(taskRowId), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    ...(signal && { signal }),
  });

  if (response.status === 409) {
    const errorData: unknown = await response.json().catch(() => ({}));
    throw oncoemrTaskAckStaleErrorFromBody(errorData);
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const message = formatErrorDetail(errorData, response.statusText);
    throw new Error(
      `Failed to acknowledge OncoEMR task (${response.status}): ${message}`,
    );
  }

  return (await response.json()) as AckOncoemrTaskResponse;
};
